import React, { useState, useMemo, useEffect, useCallback } from "react";

import { useWarehouseContext } from "./WarehouseProvider";
import { checkForAutoEscalation } from "./useWarehouse";
import { useConfiguration } from "./useConfiguration";

import { EditTitleButton } from "./components/EditTitleButton";
import { CircularProgress } from "@mui/material";

import { ReactComponent as WarningSvg } from "./svgs/warning.svg";
import { ReactComponent as InfoSvg } from "./svgs/info.svg";
import { ReactComponent as CameraSvg } from "./svgs/camera.svg";
import { ControlWeightCapture } from "./ControlWeightCapture";
import { PackagingWeightCapture } from "./PackagingWeightCapture";
import { ContentWeightCapture } from "./ContentWeightCapture";
import { ConformingWeightCapture } from "./ConformingWeightCapture";
import { NonConformPhoto } from "./NonConformPhoto";
import { SortContents } from "./SortContents";
import { CaptureScaleReadingPhoto } from "./CaptureScaleReadingPhoto";
import { Header } from "./components/Header";
import { BusyFullscreen } from "./components/BusyFullscreen";
import { useLocale } from "./useLocale";
import { GotoAllStepsButton } from "./components/GotoAllStepsButton";
import { FullscreenModal } from "./components/FullscreenModal";
import { clsx } from "clsx";
import { RejectedBanner } from "./components/RejectedBanner";
import { DropStoreModeBadge } from "./components/DropStoreModeBadge";

import { useSteps } from "./useSteps";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./components/WarehouseButtonsFooter";

// Import placeholder images
import bucketAllPlaceholder from "./images/placeholders/bucket-all.png";
import bucket2Placeholder from "./images/placeholders/bucket-2.png";
import bucket3aPlaceholder from "./images/placeholders/bucket-3a.png";
import bucket3bPlaceholder from "./images/placeholders/bucket-3b.png";
import bucket3cPlaceholder from "./images/placeholders/bucket-3c.png";
import bucket3dPlaceholder from "./images/placeholders/bucket-3d.png";
import { BUCKET_DESCRIPTIONS } from "./bucketConstants";

const TITLE = "Review Summary";

const WeightDisplay = ({ weight, error }) => {
  const locale = useLocale();

  const formatter = useMemo(
    () => new Intl.NumberFormat(locale, { style: "decimal" }),
    [locale]
  );

  return (
    <span className="flex items-baseline text-white">
      {weight !== "" ? (
        <>
          <span
            className={clsx(
              "text-[40px] font-normal font-['Space_Mono'] leading-none tracking-[-0.01em]",
              { "text-[#C12960]": error }
            )}
          >
            {formatter.format(weight)}
          </span>
          <span className="text-base font-bold font-['Manrope'] ml-2 text-[#C6CCE8] tracking-[-0.02em]">
            kg
          </span>
        </>
      ) : (
        <span className="text-[40px] font-normal font-['Space_Mono'] leading-none tracking-[-0.01em]">
          -
        </span>
      )}
    </span>
  );
};

const ActualWeightDisplay = ({ weight, controlWeight }) => {
  const locale = useLocale();

  const formatter = useMemo(
    () => new Intl.NumberFormat(locale, { style: "decimal" }),
    [locale]
  );
  const shouldDisplay = weight !== "" && controlWeight !== "";

  const negativeValue = weight - controlWeight < 0;

  return (
    shouldDisplay && (
      <p
        className={`${negativeValue ? "text-[#C12960]" : "text-[#8086A0]"} text-sm font-normal leading-[18px] flex items-baseline`}
      >
        <span className="font-normal font-['Space_Mono'] mr-1">
          {formatter.format(weight - controlWeight)} kg
        </span>
        <span className="opacity-50 ml-2">exc. control weight</span>
      </p>
    )
  );
};
export const Summary = () => {
  const {
    controlWeight,
    packagingWeight,
    contentWeight,
    sortedPictures,
    nonConformPictures,
    scaleReadingPictures,
    conformingWeight,
    nonConformingWeight,
    updateTakeback,
    clearTakeback,
    currentTakeback,
    setSelectedBucket,
  } = useWarehouseContext();

  const {
    next,
    exit,
    jumpTo,
    previous,
    hasMissingOrInvalidData,
    dropStoreMode,
  } = useSteps();

  const { isStepVisible } = useConfiguration();

  const [error, setError] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Check if takeback has been rejected
  const takebackData =
    typeof currentTakeback?.data === "object"
      ? currentTakeback?.data
      : JSON.parse(currentTakeback?.data || "{}");
  const rejectCategory = takebackData?.rejectCategory;

  const onFinish = useCallback(async () => {
    setError("");

    if (hasMissingOrInvalidData()) {
      next();
      return;
    }

    // If it's a rejected package, show the processing confirmation
    if (rejectCategory) {
      useSteps.getState().showProcessingConfirmation();
      return;
    }

    // No errors, proceed normally
    try {
      setIsUpdating(true);

      // Check if auto-escalation is needed
      if (checkForAutoEscalation()) {
        const caseId = currentTakeback?.id;
        const escalationNotes = `Weight escalation for case ${caseId}.`.trim();

        updateTakeback({
          isRejected: true,
          rejectReason: escalationNotes,
          rejectStatus: "escalated",
          rejectCategory: "escalated",
        });
      } else {
        // Normal processing
        updateTakeback();
      }

      clearTakeback();
      exit({ successMessage: "Successfully processed package" });
    } catch (e) {
      setError(
        "Finalising Take Back Failed. Please try again. If this issue persists, please log an issue."
      );
    } finally {
      setIsUpdating(false);
    }
  }, [
    hasMissingOrInvalidData,
    rejectCategory,
    next,
    clearTakeback,
    exit,
    updateTakeback,
    currentTakeback?.id,
  ]);

  // Handle Enter key to trigger Finish button
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !isUpdating) {
        e.preventDefault();
        onFinish();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isUpdating, onFinish]);

  return (
    <>
      <FullscreenModal>
        <div className="flex flex-col h-full">
          <Header>
            <div className="flex justify-between items-center w-full">
              <div className="w-10"></div>
              <span className="font-bold text-2xl text-white">{TITLE}</span>
              <div className="flex items-center gap-4">
                <DropStoreModeBadge />
                <GotoAllStepsButton />
              </div>
            </div>
          </Header>
          {rejectCategory && <RejectedBanner rejectCategory={rejectCategory} />}
          <div className="flex-1 overflow-y-auto">
            <div className="w-full p-8 flex flex-col gap-3">
              <div
                className={clsx(
                  "grid gap-4 grid-cols-1 sm:grid-cols-2",
                  dropStoreMode ? "lg:grid-cols-5" : "lg:grid-cols-4"
                )}
              >
                {isStepVisible(ControlWeightCapture) && (
                  <div className="flex flex-col bg-[#060E23] border border-[#282F44] py-2 px-4 rounded-[20px]">
                    <EditTitleButton
                      title="Control Weight"
                      onClick={() => jumpTo(ControlWeightCapture)}
                      justifyBetween
                    />
                    <WeightDisplay weight={controlWeight} />
                    {controlWeight === "" && (
                      <p className="text-xl text-[#D8A924] relative bottom-0 flex flex-row gap-2 items-center">
                        <WarningSvg style={{ width: "1rem" }} /> Weight not
                        recorded
                      </p>
                    )}
                  </div>
                )}
                {dropStoreMode && isStepVisible(PackagingWeightCapture) && (
                  <div className="flex flex-col bg-[#060E23] border border-[#282F44] py-2 px-4 rounded-[20px]">
                    <EditTitleButton
                      title="Packaging"
                      onClick={() => jumpTo(PackagingWeightCapture)}
                      justifyBetween
                    />
                    <WeightDisplay weight={packagingWeight} />

                    {packagingWeight === "" ? (
                      <p className="text-xl text-[#D8A924] relative bottom-0 flex flex-row gap-2 items-center">
                        <WarningSvg style={{ width: "1rem" }} /> Weight not
                        recorded
                      </p>
                    ) : (
                      <ActualWeightDisplay
                        weight={packagingWeight}
                        controlWeight={controlWeight}
                      />
                    )}
                  </div>
                )}
                <div className="flex flex-col bg-[#060E23] border border-[#282F44] py-2 px-4 rounded-[20px]">
                  <EditTitleButton
                    title="Total Contents"
                    onClick={() => jumpTo(ContentWeightCapture)}
                    justifyBetween
                  />
                  <WeightDisplay
                    weight={contentWeight}
                    error={conformingWeight > contentWeight}
                  />

                  {contentWeight === "" ? (
                    <p className="text-xl text-[#D8A924] relative bottom-0 flex flex-row gap-2 items-center">
                      <WarningSvg style={{ width: "1rem" }} /> Weight not
                      recorded
                    </p>
                  ) : (
                    <ActualWeightDisplay
                      weight={contentWeight}
                      controlWeight={controlWeight}
                    />
                  )}

                  {conformingWeight > contentWeight && (
                    <p className="text-xl text-[#C12960] relative bottom-0 flex flex-row gap-2 items-center">
                      <InfoSvg style={{ width: "1.5rem", fill: "#C12960" }} />{" "}
                      Total Contents is less than Conforming
                    </p>
                  )}
                </div>
                <div
                  className={clsx(
                    "flex flex-col bg-[#060E23] py-2 px-4 rounded-[20px]",
                    "border border-[#282F44]"
                  )}
                >
                  <EditTitleButton
                    title="Conforming"
                    onClick={() => jumpTo(ConformingWeightCapture)}
                    justifyBetween
                  />
                  <WeightDisplay
                    weight={conformingWeight}
                    error={conformingWeight > contentWeight}
                  />

                  {conformingWeight === "" ? (
                    <p className="text-xl text-[#D8A924] relative bottom-0 flex flex-row gap-2 items-center">
                      <WarningSvg style={{ width: "1rem" }} /> Weight not
                      recorded
                    </p>
                  ) : (
                    <>
                      <ActualWeightDisplay
                        weight={conformingWeight}
                        controlWeight={controlWeight}
                      />
                    </>
                  )}

                  {conformingWeight > contentWeight && (
                    <p className="text-xl text-[#C12960] relative bottom-0 flex flex-row gap-2 items-center">
                      <InfoSvg style={{ width: "1.5rem", fill: "#C12960" }} />{" "}
                      Conforming exceeds total contents
                    </p>
                  )}
                </div>
                <div className="flex flex-col bg-[#060E23] border border-[#282F44] py-2 px-4 rounded-[20px]">
                  <EditTitleButton
                    title="Non-Conforming"
                    onClick={() => null}
                    justifyBetween
                    canEdit={false}
                  />
                  <div className="mt-3">
                    <WeightDisplay
                      weight={nonConformingWeight}
                      error={nonConformingWeight < 0}
                    />
                  </div>
                  {nonConformingWeight < 0 && (
                    <p className="text-xl text-[#C12960] relative bottom-0 flex flex-row gap-2 items-center">
                      <InfoSvg style={{ width: "1.5rem", fill: "#C12960" }} />{" "}
                      Negative weight
                    </p>
                  )}
                  {nonConformingWeight === "" && (
                    <p className="text-xl relative bottom-0 flex flex-row gap-2 items-center">
                      <InfoSvg style={{ width: "1.5rem" }} /> Awaiting all
                      weights
                    </p>
                  )}
                </div>
              </div>

              {/* Divider between Weights and Photos sections */}
              <div className="w-full h-[1px] bg-[#282F44] mt-5"></div>

              {/* Photos Section */}
              <div className="flex flex-col gap-6">
                <h2 className="text-[23px] font-bold text-white leading-[30px] tracking-[-0.0038em] mt-4">
                  Photos
                </h2>

                <div className="flex flex-wrap gap-4">
                  {/* Non-Conform Photos */}
                  {nonConformPictures.length > 0 ? (
                    nonConformPictures.map((image, index) => (
                      <div
                        key={`nonconform-${index}`}
                        className="flex-shrink-0 cursor-pointer"
                        onClick={() => jumpTo(NonConformPhoto)}
                      >
                        <div className="w-[140px] h-[105px] mb-2">
                          <img
                            src={image}
                            alt={`Non-Conform ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>
                        <p className="text-sm text-white text-center">
                          Non-Conform
                        </p>
                      </div>
                    ))
                  ) : (
                    <div
                      className="flex-shrink-0 cursor-pointer"
                      onClick={() => jumpTo(NonConformPhoto)}
                    >
                      <div className="w-[140px] h-[105px] mb-2 bg-[#060E23] rounded-lg border flex items-center justify-center">
                        <CameraSvg className="fill-white/50 w-10 h-10" />
                      </div>
                      <p className="text-sm text-white text-center">
                        Non-Conform
                      </p>
                    </div>
                  )}

                  {/* Conform Photo (Bucket 1) */}
                  {(() => {
                    const conformImage = sortedPictures?.all;
                    return conformImage ? (
                      <div
                        className="flex-shrink-0 cursor-pointer"
                        onClick={() => {
                          setSelectedBucket("all");
                          jumpTo(SortContents);
                        }}
                      >
                        <div className="w-[140px] h-[105px] mb-2">
                          <img
                            src={conformImage}
                            alt="Conform"
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-white">
                            {BUCKET_DESCRIPTIONS.all.heading}
                          </p>
                          <p className="text-xs text-[#C6CCE8]">
                            {BUCKET_DESCRIPTIONS.all.description}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="flex-shrink-0 cursor-pointer"
                        onClick={() => {
                          setSelectedBucket("all");
                          jumpTo(SortContents);
                        }}
                      >
                        <div className="w-[140px] h-[105px] mb-2">
                          <img
                            src={bucketAllPlaceholder}
                            alt="Conform placeholder"
                            className="w-full h-full object-cover rounded-lg opacity-50"
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-white">
                            {BUCKET_DESCRIPTIONS.all.heading}
                          </p>
                          <p className="text-xs text-[#C6CCE8]">
                            {BUCKET_DESCRIPTIONS.all.description}
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Scale Reading Photos */}
                  {scaleReadingPictures.length > 0 ? (
                    scaleReadingPictures.map((image, index) => (
                      <div
                        key={`scale-${index}`}
                        className="flex-shrink-0 cursor-pointer"
                        onClick={() => jumpTo(CaptureScaleReadingPhoto)}
                      >
                        <div className="w-[140px] h-[105px] mb-2">
                          <img
                            src={image}
                            alt={`Scale ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>
                        <p className="text-sm text-white text-center">
                          Scale Reading
                        </p>
                      </div>
                    ))
                  ) : (
                    <div
                      className="flex-shrink-0 cursor-pointer"
                      onClick={() => jumpTo(CaptureScaleReadingPhoto)}
                    >
                      <div className="w-[140px] h-[105px] mb-2 bg-[#060E23] rounded-lg border flex items-center justify-center">
                        <CameraSvg className="fill-white/50 w-10 h-10" />
                      </div>
                      <p className="text-sm text-white text-center">
                        Scale Reading
                      </p>
                    </div>
                  )}

                  {/* Remaining Bucket Photos (2-6) */}
                  {[
                    {
                      key: "bucket2",
                      num: 2,
                      label: "Bucket 2",
                      placeholder: bucket2Placeholder,
                    },
                    {
                      key: "bucket3a",
                      num: 3,
                      label: "Bucket 3",
                      placeholder: bucket3aPlaceholder,
                    },
                    {
                      key: "bucket3c",
                      num: 4,
                      label: "Bucket 4",
                      placeholder: bucket3bPlaceholder,
                    },
                    {
                      key: "bucket3b",
                      num: 5,
                      label: "Bucket 5",
                      placeholder: bucket3cPlaceholder,
                    },
                    {
                      key: "bucket3d",
                      num: 6,
                      label: "Bucket 6",
                      placeholder: bucket3dPlaceholder,
                    },
                  ].map(({ key, num, label, placeholder }) => {
                    const bucketImage = sortedPictures?.[key];
                    return bucketImage ? (
                      <div
                        key={key}
                        className="flex-shrink-0 cursor-pointer"
                        onClick={() => {
                          setSelectedBucket(key);
                          jumpTo(SortContents);
                        }}
                      >
                        <div className="w-[140px] h-[105px] mb-2">
                          <img
                            src={bucketImage}
                            alt={label}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-white">
                            {BUCKET_DESCRIPTIONS[key].heading}
                          </p>
                          <p className="text-xs text-[#C6CCE8]">
                            {BUCKET_DESCRIPTIONS[key].description}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div
                        key={key}
                        className="flex-shrink-0 cursor-pointer"
                        onClick={() => {
                          setSelectedBucket(key);
                          jumpTo(SortContents);
                        }}
                      >
                        <div className="w-[140px] h-[105px] mb-2">
                          <img
                            src={placeholder}
                            alt={`${label} placeholder`}
                            className="w-full h-full object-cover rounded-lg opacity-50"
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-white">
                            {BUCKET_DESCRIPTIONS[key].heading}
                          </p>
                          <p className="text-xs text-[#C6CCE8]">
                            {BUCKET_DESCRIPTIONS[key].description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <WarehouseButtonsFooter error={error}>
            <WarehouseButtonGroup>
              <WarehouseButton
                variant="default"
                onClick={previous}
                disabled={isUpdating}
              >
                Back
              </WarehouseButton>
              <WarehouseButton
                variant="primary"
                onClick={onFinish}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <CircularProgress size={24} />
                ) : rejectCategory ? (
                  "Complete Processing"
                ) : (
                  "Finish"
                )}
              </WarehouseButton>
            </WarehouseButtonGroup>
          </WarehouseButtonsFooter>
        </div>
      </FullscreenModal>
      {isUpdating && <BusyFullscreen message="Finalising Take Back" />}
    </>
  );
};
Summary.title = TITLE;
